import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from "@angular/core";
import {
    EventCreationFormService,
    EventFormConfigPutQuery,
    EventFormConfigResponseItem,
} from "../../../../services/event.creation.form.service";
import { S25Util } from "../../../../util/s25-util";
import { TypeManagerDecorator } from "../../../../main/type.map.service";

@TypeManagerDecorator("s25-ng-event-form-config")
@Component({
    selector: "s25-ng-event-form-config",
    template: `
        @if (isInit) {
            <div>
                <div s25-ng-auto-scroll>
                    <div class="configSettings">
                        <h5>Configuration Settings</h5>
                        <label>
                            <span>Configuration Name:</span>
                            <s25-ng-editable-text
                                [allowEmpty]="false"
                                [(val)]="config.configuration_name"
                            ></s25-ng-editable-text>
                        </label>
                        <label>
                            <span>Help Message:</span>
                            <s25-ng-editable-richtext [(val)]="config.configuration_message"></s25-ng-editable-richtext>
                        </label>
                        <div class="toggles">
                            <label
                                ><span>Default Config:</span>
                                <s25-toggle-button
                                    [disabled]="!canChangeIsDefault"
                                    [(value)]="config.is_default"
                                ></s25-toggle-button>
                            </label>

                            @for (toggle of toggles; track toggle.key) {
                                <label>
                                    <span>
                                        {{ toggle.label }}:
                                        @if (toggle.info) {
                                            <s25-ng-info-message [isPopover]="true">{{
                                                toggle.info
                                            }}</s25-ng-info-message>
                                        }
                                    </span>

                                    <s25-toggle-button [(value)]="config[toggle.key]"></s25-toggle-button>
                                </label>
                            }
                        </div>
                        <label>
                            <span>Repeating Pattern Help Message:</span>
                            <s25-ng-editable-richtext [(val)]="config.pattern_help"></s25-ng-editable-richtext>
                        </label>
                        <div class="configRow">
                            <label id="default-event-type-label"><span>Default Event Type:</span></label>
                            <s25-ng-dropdown-search-criteria
                                [type]="'eventTypes'"
                                [(value)]="config.default_event_type"
                                aria-labelledby="default-event-type-label"
                            />
                            <button class="aw-button aw-button--outline" (click)="onResetDefaultEventType()">
                                Remove
                            </button>
                        </div>
                    </div>
                    <h5>Item Settings</h5>
                    <div s25-ng-dnd-sortable [items]="config.item">
                        @for (item of config.item; track rerenderOnReorder(i, item); let i = $index) {
                            <div s25-ng-dnd-sortable-item [index]="i" class="configItemRow">
                                <s25-ng-icon [type]="'dragHandle'" [ariaLabel]="false" />
                                <h6 class="ngBold">{{ item.formalName }}</h6>
                                <label class="displayName">
                                    Display Name:
                                    <s25-ng-editable-text [(val)]="item.name"></s25-ng-editable-text>
                                </label>
                                <label>
                                    Visibility Level:
                                    <select class="cn-form__control" [(ngModel)]="item.visibility">
                                        <option value="invisible">Not Visible</option>
                                        <option value="visible">View Only</option>
                                        <option value="editable">Editable</option>
                                        <option value="required">Required</option>
                                    </select>
                                </label>
                                @if (item.id !== "affirmation") {
                                    <label>
                                        <s25-ng-checkbox [(value)]="item.autoOpen"></s25-ng-checkbox>
                                        Auto Open Help Message
                                    </label>
                                }
                                <label class="helpMessage">
                                    {{ item.id !== "affirmation" ? "Help Message:" : "Message" }}
                                    <s25-ng-editable-richtext
                                        [(val)]="item.message"
                                        *s25-ng-trigger-rerender="i"
                                    ></s25-ng-editable-richtext>
                                </label>
                            </div>
                        }
                    </div>
                </div>
                <div class="buttons">
                    <button class="aw-button aw-button--primary" (click)="onSave()">Save</button>
                    <button class="aw-button aw-button--outline cancel" (click)="onCancel()">Cancel</button>
                </div>
            </div>
        }
    `,
    styles: `
        .toggles {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(23em, 1fr));
            column-gap: 10rem;
            row-gap: 0.5rem;
            margin-block-end: 2rem;

            & > label {
                display: grid;
                grid-template-columns: 1fr auto;

                span {
                    align-self: center;
                }
            }
        }

        label,
        label > span {
            display: block;
        }

        s25-toggle-button {
            display: block;
        }

        s25-ng-dropdown-search-criteria {
            display: inline-block;
            min-width: 15em;
        }

        s25-ng-dropdown-search-criteria + button {
            margin-left: 1em;
        }

        .configRow {
            margin-bottom: 2em;
        }

        .configItemRow {
            display: grid;
            grid-template-columns: 20px 150px 1fr 1fr 1fr;
            grid-template-rows: auto auto;
            column-gap: 1.5em;
            align-items: start;
            padding-top: 0.5em;
            border-bottom: 1px solid #ddd;
        }

        .configItemRow > .helpMessage {
            grid-row-start: 3;
            grid-column: 3 / span 3;
        }

        .displayName {
            min-width: 0;
        }

        .buttons {
            padding-top: 1em;
            display: flex;
            gap: 0.5em;
            position: sticky;
            bottom: 0;
            width: fit-content;
            z-index: 10;
        }

        .buttons .cancel:not(:hover, :focus) {
            background-color: white !important;
        }

        ::ng-deep #s25.nm-party--on .buttons .cancel:not(:hover, :focus) {
            background-color: #3d3d46 !important;
        }

        ::ng-deep
            s25-ng-event-form-config
            s25-ng-editable-richtext
            .s25-editable-shared-container.c-editable-text.editable.editable-click {
            height: 5em;
            overflow: hidden;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25EventFormConfigComponent implements OnInit {
    @Input() id?: number;
    @Input() mode: "create" | "copy" | "edit" = "edit";

    @Output() saved = new EventEmitter<void>();
    @Output() cancelled = new EventEmitter<void>();

    isInit = false;
    config: Config;
    canChangeIsDefault = true;
    protected readonly toggles = toggles;

    constructor(private changeDetector: ChangeDetectorRef) {}

    async ngOnInit() {
        this.config = this.mode === "create" ? this.defaultConfig : await this.getConfig(this.id);
        this.canChangeIsDefault = !this.config.is_default; // Cannot unset default. You have to set another config to default instead
        this.isInit = true;
        this.changeDetector.detectChanges();
    }

    async getConfig(id: number): Promise<Config> {
        const data = await EventCreationFormService.getConfig(id);
        return {
            configuration_id: this.mode === "copy" ? null : data.configuration_id,
            configuration_name: (data.configuration_name || "") + (this.mode === "copy" ? " copy" : ""),
            configuration_message: data.configuration_message || "",
            auto_add_cust_atrb: !!data.auto_add_cust_atrb,
            has_spans_midnight: !!data.has_spans_midnight,
            has_all_day: !!data.has_all_day,
            expand_additional_time: !!data.expand_additional_time,
            has_multi_profile: !!data.has_multi_profile,
            has_recommended_resource: !!data.has_recommended_resource,
            has_recommended_location: !!data.has_recommended_location,
            exclude_express_locations: !!data.exclude_express_locations,
            has_silent_save: !!data.has_silent_save,
            has_pattern: !!data.has_pattern,
            pattern_help: data.pattern_help || "",
            is_default: !!data.is_default,
            disable_rules: !!data.disable_rules,
            check_hide_conflicts: !!data.check_hide_conflicts,
            hide_hide_conflicts: !!data.hide_hide_conflicts,
            check_hide_request_conflicts: !!data.check_hide_request_conflicts,
            hide_hide_request_conflicts: !!data.hide_hide_request_conflicts,
            check_enforce_headcount: !!data.check_enforce_headcount,
            hide_enforce_headcount: !!data.hide_enforce_headcount,
            optional_attributes: !!data.optional_attributes,
            default_event_type: data.default_event_type,
            item: this.extractItems(data.item),
        };
    }

    getVisibility(item: EventFormConfigResponseItem): ConfigItem["visibility"] {
        if (!item.visible) return "invisible";
        if (!!item.no_edit) return "visible";
        if (!!item.required) return "required";
        return "editable";
    }

    extractItems(items: EventFormConfigResponseItem[]): ConfigItem[] {
        return items.map((item) => ({
            id: item.item_name,
            formalName: EventCreationFormService.ConfigFormItemData[item.item_name].display,
            name: item.item_display,
            visibility: this.getVisibility(item),
            autoOpen: !!item.message_open,
            message: item.item_message ?? "",
            initialOrder: item.sort_order ?? EventCreationFormService.ConfigFormItemData[item.item_name].order,
        }));
    }

    get defaultConfig(): Config {
        return {
            configuration_id: null,
            configuration_name: "New Config",
            configuration_message: "",
            auto_add_cust_atrb: false,
            has_spans_midnight: false,
            expand_additional_time: false,
            has_all_day: false,
            has_multi_profile: false,
            has_recommended_resource: false,
            has_silent_save: false,
            has_pattern: false,
            pattern_help: "",
            is_default: false,
            disable_rules: false,
            check_hide_conflicts: false,
            hide_hide_conflicts: false,
            check_hide_request_conflicts: false,
            hide_hide_request_conflicts: false,
            check_enforce_headcount: false,
            hide_enforce_headcount: false,
            optional_attributes: false,
            has_recommended_location: false,
            exclude_express_locations: false,
            default_event_type: null,
            item: Object.entries(EventCreationFormService.ConfigFormItemData)
                .filter(([_, item]: any) => !item.deprecated)
                .map(([id, item]) => ({
                    id: id as keyof typeof EventCreationFormService.ConfigFormItemData,
                    formalName: item.display,
                    name: item.display,
                    visibility: "invisible",
                    autoOpen: false,
                    message: "",
                    initialOrder: item.order,
                })),
        };
    }

    onResetDefaultEventType() {
        this.config.default_event_type = null;
        this.changeDetector.detectChanges();
    }

    async onSave() {
        if (!this.validate(this.config)) return;
        const payload = this.convertConfig(this.config);
        const ok = await EventCreationFormService.putConfig(payload, this.config.configuration_id).catch((error) => {
            S25Util.showError(error);
        });
        if (!ok) return;
        this.saved.emit();
    }

    onCancel() {
        this.cancelled.emit();
    }

    convertConfig(config: Config): EventFormConfigPutQuery {
        return {
            root: {
                config: {
                    configuration_message: config.configuration_message,
                    has_recommended_resource: config.has_recommended_resource ? 1 : 0,
                    has_recommended_location: config.has_recommended_location ? 1 : 0,
                    exclude_express_locations: config.exclude_express_locations ? 1 : 0,
                    has_pattern: config.has_pattern ? 1 : 0,
                    is_default: config.is_default ? 1 : 0,
                    has_multi_profile: config.has_multi_profile ? 1 : 0,
                    has_silent_save: config.has_silent_save ? 1 : 0,
                    pattern_help: config.pattern_help,
                    has_spans_midnight: config.has_spans_midnight ? 1 : 0,
                    expand_additional_time: config.expand_additional_time ? 1 : 0,
                    auto_add_cust_atrb: config.auto_add_cust_atrb ? 1 : 0,
                    configuration_name: config.configuration_name,
                    has_all_day: config.has_all_day ? 1 : 0,
                    disable_rules: config.disable_rules ? 1 : 0,
                    check_hide_conflicts: config.check_hide_conflicts ? 1 : 0,
                    hide_hide_conflicts: config.hide_hide_conflicts ? 1 : 0,
                    check_hide_request_conflicts: config.check_hide_request_conflicts ? 1 : 0,
                    hide_hide_request_conflicts: config.hide_hide_request_conflicts ? 1 : 0,
                    check_enforce_headcount: config.check_enforce_headcount ? 1 : 0,
                    hide_enforce_headcount: config.hide_enforce_headcount ? 1 : 0,
                    optional_attributes: config.optional_attributes ? 1 : 0,
                    default_event_type: config.default_event_type,
                    configuration_id: config.configuration_id,
                },
                item: config.item
                    .map((item, i) => [item, i])
                    .map(([item, i]: [ConfigItem, number]) => ({
                        message_open: item.autoOpen ? 1 : 0,
                        item_display: item.name,
                        no_edit: item.visibility === "visible" ? 1 : 0,
                        item_name: item.id,
                        required: item.visibility === "required" ? 1 : 0,
                        item_message: item.message,
                        sort_order: i + 1,
                        visible: item.visibility !== "invisible" ? 1 : 0,
                    })),
            },
        };
    }

    validate(config: Config): boolean {
        const typeItem = config.item.find((item) => item.id === "type");
        if (
            !config.default_event_type &&
            typeItem &&
            typeItem.visibility !== "required" &&
            typeItem.visibility !== "editable"
        ) {
            alert(
                `The ${typeItem.name} form item is not editable and requires a default value. Please set a default for this item.`,
            );
            return false;
        }
        return true;
    }

    // This is needed to avoid the list jumping down when dragging an item up
    rerenderOnReorder(index: number, item: ConfigItem) {
        return `${index} ${item.id}`;
    }
}

interface Config {
    configuration_id: number;
    configuration_name: string;
    configuration_message: string;
    auto_add_cust_atrb: boolean;
    has_spans_midnight: boolean;
    has_all_day: boolean;
    has_multi_profile: boolean;
    expand_additional_time: boolean;
    has_recommended_resource: boolean;
    has_recommended_location: boolean;
    exclude_express_locations: boolean;
    has_silent_save: boolean;
    has_pattern: boolean;
    pattern_help: string;
    item: ConfigItem[];
    is_default: boolean;
    disable_rules: boolean;
    check_hide_conflicts: boolean;
    hide_hide_conflicts: boolean;
    check_hide_request_conflicts: boolean;
    hide_hide_request_conflicts: boolean;
    check_enforce_headcount: boolean;
    hide_enforce_headcount: boolean;
    optional_attributes: boolean;
    default_event_type: {
        itemId: number;
        itemName: string;
    };
}

interface ConfigItem {
    id: keyof typeof EventCreationFormService.ConfigFormItemData;
    formalName: string;
    name: string;
    visibility: "required" | "editable" | "visible" | "invisible";
    autoOpen: boolean;
    message: string;
    initialOrder: number;
}

const toggles = [
    {
        key: "auto_add_cust_atrb",
        label: "Automatically Add Custom Attributes",
        info: "Automatically adds all custom attributes from the event type",
    },
    {
        key: "optional_attributes",
        label: "All Custom Attributes Are Optional",
        info: "Makes all custom attributes optional regardless of any other settings",
    },
    { key: "has_spans_midnight", label: "Allow Spans Midnight Events", info: "Allows events to span midnight" },
    {
        key: "expand_additional_time",
        label: "Expand Additional Time By Default",
        info: "Automatically expands the additional time field by default",
    },
    { key: "has_all_day", label: "Show All Day Toggle", info: "Shows a toggle for all day events" },
    { key: "has_multi_profile", label: "Allow Add/Remove Segments", info: "Allows adding/removing segments" },
    {
        key: "has_recommended_resource",
        label: "Use Recommended Resources",
        info: "Recommended resources added by rules will be shown instead of the resource search",
    },
    {
        key: "has_recommended_location",
        label: "Use Recommended Locations",
        info: "Recommended locations added by rules will be shown instead of the location search",
    },
    {
        key: "exclude_express_locations",
        label: "Hide Express Locations",
        info: "Hides express locations in the location search",
    },
    { key: "has_silent_save", label: "Allow Silent Save", info: "Saving does not trigger Event Save scenarios" },
    { key: "disable_rules", label: "Disable Rules", info: "No rules will be applied" },
    {
        key: "check_hide_conflicts",
        label: "Hide Conflicts By Default",
        info: `The "Hide Conflicts" checkbox in the location search is checked by default`,
    },
    {
        key: "hide_hide_conflicts",
        label: `Hide "Hide Conflicts" Checkbox`,
        info: `Hides the "Hide Conflicts" checkbox in the location search`,
    },
    {
        key: "check_enforce_headcount",
        label: "Enforce Headcount By Default",
        info: `The "Enforce Headcount" checkbox in the location search is checked by default`,
    },
    {
        key: "hide_enforce_headcount",
        label: `Hide "Enforce Headcount" Checkbox`,
        info: `Hides the "Enforce Headcount" checkbox in the location search`,
    },
    {
        key: "check_hide_request_conflicts",
        label: "Hide Request Conflicts By Default",
        info: `The "Hide Request Conflicts" checkbox in the location search is checked by default`,
    },
    {
        key: "hide_hide_request_conflicts",
        label: `Hide "Hide Request Conflicts" Checkbox`,
        info: `Hides the "Hide Request Conflicts" checkbox in the location search`,
    },
    {
        key: "has_pattern",
        label: `Allow Repeating Pattern`,
        info: `Allows the user to create repeating events`,
    },
] as const;
